import { useRef, useState } from "react";
import './styles.less'

export type NpsCategory = "detractor" | "pasivo" | "promotor";

export const getNpsCategory = (score: number): NpsCategory => {
    if (score <= 6) return "detractor"
    if (score <= 8) return "pasivo"
    return "promotor"
}

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const

interface NpsScaleProps {
    value?: number | null;
    onChange?: (score: number, category: NpsCategory) => void;
    ariaLabel?: string;
}

const NpsScale = ({
    value,
    onChange,
    ariaLabel = "Puntuación NPS de 0 a 10",
}: NpsScaleProps) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<number | null>(null);
    const selected = isControlled ? value : internalValue;
    const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

    const select = (score: number) => {
        if (!isControlled) setInternalValue(score);
        onChange?.(score, getNpsCategory(score));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const lastIndex = SCORES.length - 1;
        let nextIndex: number | null = null;

        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                nextIndex = index === lastIndex ? 0 : index + 1;
                break;
            case "ArrowLeft":
            case "ArrowUp":
                nextIndex = index === 0 ? lastIndex : index - 1;
                break;
            case "Home":
                nextIndex = 0;
                break;
            case "End":
                nextIndex = lastIndex;
                break;
            default:
                return;
        }

        event.preventDefault();
        buttonsRef.current[nextIndex]?.focus();
        select(SCORES[nextIndex]);
    };
    const focusableIndex = selected === null ? 0 : SCORES.indexOf(selected as (typeof SCORES)[number]);

    return (
        <>
            <div role="radiogroup" aria-label={ariaLabel} className="npsScale">
                {SCORES.reduce<React.ReactNode[]>((acc, score, index) => {
                    const isSelected = selected === score;

                    acc.push(
                        <button
                            key={score}
                            ref={(node) => {
                                buttonsRef.current[index] = node;
                            }}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            aria-label={`${score}`}
                            tabIndex={index === focusableIndex ? 0 : -1}
                            onClick={() => select(score)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            className={`npsScale__option npsScale__option--${getNpsCategory(score)}${
                                isSelected ? " npsScale__option--selected" : ""
                            }`}
                        >
                            {score}
                        </button>
                    );

                    if (index === 5) {
                        acc.push(<span key="break" className="npsScale__break" aria-hidden="true" />);
                    }

                    return acc;
                }, [])}
            </div>
            <div className="npsScale__footer">
                <span>Nada Probable</span>
                <span>Muy probable</span>
            </div>
        </>
    );
}
export default NpsScale